import * as THREE from 'three'
import { COLORS } from '../config'

export interface HostileProjectileRenderState {
  readonly position: THREE.Vector3
}

export class HostileProjectileBatch {
  private readonly geometry = new THREE.SphereGeometry(0.2, 8, 6)
  private readonly material = new THREE.MeshStandardMaterial({
    color: 0x330402,
    emissive: COLORS.threat,
    emissiveIntensity: 7,
    toneMapped: false,
  })
  private readonly batch: THREE.InstancedMesh
  private readonly matrix = new THREE.Matrix4()
  private disposed = false

  constructor(
    private readonly scene: THREE.Scene,
    private readonly capacity = 16,
  ) {
    this.batch = new THREE.InstancedMesh(this.geometry, this.material, this.capacity)
    this.batch.count = 0
    this.batch.visible = false
    this.batch.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
    this.scene.add(this.batch)
  }

  sync(projectiles: readonly HostileProjectileRenderState[]): void {
    if (this.disposed) throw new Error('Hostile projectile batch has been disposed')
    if (projectiles.length > this.capacity) {
      throw new Error(`Hostile projectile batch capacity ${this.capacity} exceeded by ${projectiles.length}`)
    }

    for (let index = 0; index < projectiles.length; index += 1) {
      const projectile = projectiles[index]
      if (!projectile) continue
      const { position } = projectile
      this.matrix.makeTranslation(position.x, position.y, position.z)
      this.batch.setMatrixAt(index, this.matrix)
    }
    this.batch.count = projectiles.length
    this.batch.visible = projectiles.length > 0
    this.batch.instanceMatrix.needsUpdate = true
    if (projectiles.length > 0) {
      this.batch.computeBoundingBox()
      this.batch.computeBoundingSphere()
    }
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.scene.remove(this.batch)
    this.batch.dispose()
    this.geometry.dispose()
    this.material.dispose()
  }
}
